/**
 * AudienceActionMenu — Shared dropdown menu for audience cards.
 *
 * Renders the 3-dot menu with "Delete Audience" action. Used by both
 * CinematicAudienceCard (via the actionMenu slot) and the search results grid.
 *
 * Publishing / unpublishing is managed on the audience detail page.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LogOut, MoreVertical, Pencil, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CardOverlayDeleteButton } from '@/components/ui/CardOverlayDeleteButton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import LeaveSharedAudienceDialog from './LeaveSharedAudienceDialog'

interface AudienceActionMenuProps {
  audience: {
    id: string
    name: string
    status?: string
    /**
     * When true, the viewer may edit and delete this audience.
     * When false (viewer is a read-only collaborator), shows "Leave audience" instead.
     */
    canEdit?: boolean
  }
  deletingAudienceId: string | null
  handleDeleteAudience: (e: React.MouseEvent, id: string) => void
  /** Called after the user successfully leaves a shared audience */
  onLeft?: () => void
}

export default function AudienceActionMenu({
  audience,
  deletingAudienceId,
  handleDeleteAudience,
  onLeft,
}: AudienceActionMenuProps) {
  const { t } = useTranslation('app')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const navigate = useNavigate()

  const isError = audience.status === 'error'
  // canEdit defaults to true for backward compatibility (owned audiences).
  const canEdit = audience.canEdit !== false

  return (
    <>
      {isError && canEdit ? (
        <CardOverlayDeleteButton
          ariaLabel={t('audienceActions.deleteAria')}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setConfirmDelete(true)
          }}
        />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 hover:text-white border-0"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-52"
            onClick={(e) => e.stopPropagation()}
          >
            {!canEdit ? (
              <DropdownMenuItem
                onSelect={() => setConfirmLeave(true)}
                className="text-destructive dark:text-red-400 focus:text-destructive dark:focus:text-red-400"
                data-testid="leave-audience-menu-item"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('audienceActions.leave')}</span>
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem
                  onSelect={(e) => {
                    (e as unknown as React.MouseEvent).stopPropagation?.()
                    navigate(`/audiences/${audience.id}/edit-stage`)
                  }}
                >
                  <Pencil className="h-4 w-4" />
                  <span>{t('audienceActions.edit')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setConfirmDelete(true)}
                  className="text-destructive dark:text-red-400 focus:text-destructive dark:focus:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>{t('audienceActions.delete')}</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Leave shared audience confirmation */}
      <LeaveSharedAudienceDialog
        open={confirmLeave}
        onOpenChange={setConfirmLeave}
        audienceId={audience.id}
        audienceName={audience.name}
        onLeft={() => onLeft?.()}
      />

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent
          className="w-[540px] gap-0 rounded-[16px] border-border dark:border-[hsl(var(--card-inset))] bg-background dark:bg-card p-0 shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-end p-3">
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="text-muted-foreground hover:text-foreground dark:text-white/70 dark:hover:text-white transition-colors"
              aria-label={t('close', { ns: 'common' })}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-7 px-[60px] pb-10">
            <AlertDialogHeader className="items-center gap-3 space-y-0">
              <AlertDialogTitle className="font-display text-2xl font-normal leading-[1.2] tracking-[-0.48px] text-foreground dark:text-white text-center">
                {t('audienceActions.removeQ')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base leading-[1.6] text-muted-foreground text-center">
                This will permanently remove &ldquo;{audience.name}&rdquo;. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="flex items-center justify-center gap-4">
              <AlertDialogAction
                className="h-10 rounded-[6px] px-4 text-base font-medium bg-cta text-cta-foreground hover:bg-cta/90"
                onClick={(e) => {
                  handleDeleteAudience(e, audience.id)
                  setConfirmDelete(false)
                }}
              >
                {deletingAudienceId === audience.id ? t('deleting', { ns: 'common' }) : t('remove', { ns: 'common' })}
              </AlertDialogAction>
              <AlertDialogCancel
                className="h-10 rounded-[6px] border-foreground dark:border-input-focus bg-transparent px-4 text-base font-medium text-foreground dark:text-input-focus hover:bg-transparent hover:text-foreground dark:hover:text-input-focus"
              >
                {t('neverMind', { ns: 'common' })}
              </AlertDialogCancel>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
